import * as THREE from 'three'
import gsap from 'gsap'
import Lenis from 'lenis'
import { params } from '../config/persistence.js'
import { isDebug } from './overlay.js'

const TEXT_1 = `L'aria che respiri,<br>naturalmente pura`
const TEXT_2 = `La tecnologia Carbon.Zeo Microtech utilizza carbone attivo e zeolite, materiali di origine naturale, per assorbire odori e vapore con la massima efficacia.`

// Proietta un rect schermo (px) sul piano z=0 del mondo (camera fissa a z=18, fov 35)
const CAMERA_Z = 18
const CAMERA_FOV = 35

const screenRectToWorldBox = (rect, padding, sizeScale = 1.05) => {
    const vw = window.innerWidth
    const vh = window.innerHeight
    const worldHalfH = Math.tan((CAMERA_FOV * Math.PI) / 360) * CAMERA_Z
    const worldHalfW = worldHalfH * (vw / vh)
    const left = rect.left - padding
    const right = rect.right + padding
    const top = rect.top - padding
    const bottom = rect.bottom + padding
    const ndcX = ((left + right) / 2 / vw) * 2 - 1
    const ndcY = -(((top + bottom) / 2 / vh) * 2 - 1)
    return {
        x: ndcX * worldHalfW,
        y: ndcY * worldHalfH,
        hx: ((right - left) / vw) * worldHalfW * sizeScale,
        hy: ((bottom - top) / vh) * worldHalfH * sizeScale,
    }
}

const clamp01 = (v) => Math.min(Math.max(v, 0), 1)
const ramp = (p, a, b) => clamp01((p - a) / (b - a))
const smooth = (t) => t * t * (3 - 2 * t)
const mix = (a, b, t) => a + (b - a) * t

// --- Profilo di repulsione editabile (curva di Bezier a 4 punti) ---
// I punti sono del fianco DESTRO in coordinate normalizzate: x in unita' di hx
// (semi-larghezza del box), y in unita' di hy (semi-altezza). Il lato sinistro e'
// lo specchio. P0 = punta in basso (sull'asse, giunzione sinuosa), P1/P2 = maniglie
// di controllo dell'angolazione, P3 = ancora in alto.
const BEZIER_KEY = 'falmec-carbon-bezier'
const PROFILE_N = 64
const DEFAULT_CTRL = [
    { x: 0.00, y: -1.56 },
    { x: 1.56, y: 0.88 },
    { x: 0.00, y: 2.97 },
    { x: 0.65, y: 8.24 },
]
const cubic = (a, b, c, d, t) => {
    const u = 1 - t
    return u * u * u * a + 3 * u * u * t * b + 3 * u * t * t * c + t * t * t * d
}

export const createCarbonPage = () => {
    // Abilita lo scroll nativo (la home resta overflow:hidden)
    document.documentElement.classList.add('carbon-scroll-page')

    // Spacer di scroll: la coreografia è interamente guidata dal progresso
    const main = document.createElement('main')
    main.className = 'carbon-main carbon-scroll-space'
    document.body.appendChild(main)
    // In debug lo spacer non deve intercettare i click, altrimenti (essendo sopra
    // il canvas WebGL) blocca i gizmo TransformControls delle barre di aspirazione.
    // Lo scroll nativo continua a funzionare comunque.
    if (isDebug) main.style.pointerEvents = 'none'

    // Testi fissi al centro dello schermo, pilotati in opacità dallo scroll
    const makeFixedText = (html) => {
        const wrap = document.createElement('div')
        wrap.className = 'carbon-fixed-text'
        wrap.innerHTML = html
        wrap.style.opacity = 0
        document.body.appendChild(wrap)
        return wrap
    }
    const wrap1 = makeFixedText(`<h2 class="carbon-text carbon-text-1">${TEXT_1}</h2>`)
    const wrap2 = makeFixedText(`<p class="carbon-text carbon-text-2">${TEXT_2}</p>`)
    const textEl1 = wrap1.firstElementChild
    const textEl2 = wrap2.firstElementChild

    // --- Lenis smooth scroll ---
    const lenis = new Lenis()
    gsap.ticker.add((time) => lenis.raf(time * 1000))
    gsap.ticker.lagSmoothing(0)

    // --- La simulazione gira SEMPRE a regime, fin dal caricamento: lo scroll
    // non la avvia/ferma mai, cambia solo l'opacita' delle particelle (fade a
    // tenda dall'alto verso il basso, vedi fragment shader). Cosi' quando il
    // fade entra le particelle sono gia' distribuite e in movimento.
    const targetSpawn = params.respawnRate
    const spawnRegion = { value: 1 } // fisso: si rinasce solo offscreen, sopra

    // L'aspirazione e' sempre attiva sulla pagina carbon
    params.suctionBarsEnabled = true

    const compute = window._getCompute && window._getCompute()
    if (compute) compute.particlesVariable.material.uniforms.uDecayRate.value = targetSpawn
    if (isDebug) window._fadeState.value = 1.0

    // --- Stato della sagoma unica (segue lo scrub, lerpato per fluidità) ---
    const st = { x: 0, y: 0, hx: 0, hy: 0, cx: 0, cy: 0, chx: 0, chy: 0, strength: 0, tipUp: 0, tipDown: 0, active: false }
    const smoothed = [st] // il debug overlay itera su questo array

    // Punti di controllo (ripristinati dai tweak salvati in debug)
    let bezierCtrl = DEFAULT_CTRL.map((p) => ({ ...p }))
    if (isDebug) {
        try {
            const saved = JSON.parse(localStorage.getItem(BEZIER_KEY))
            if (Array.isArray(saved) && saved.length === 4)
                bezierCtrl = saved.map((p) => ({ x: +p.x, y: +p.y }))
        } catch (e) {}
    }
    let profileTex = null
    let profileYMin = 0
    let profileYMax = 1
    let profileDirty = true
    let dragIndex = -1
    const handleScreen = [] // posizioni px delle 4 maniglie per l'hit-test del drag

    // Larghezza del profilo alla quota normalizzata ty: scandisce i campioni della
    // curva e prende la x piu' esterna (robusto anche se la curva non e' monotona)
    const sampleXatY = (ys, xs, ty) => {
        let best = 0
        for (let i = 0; i < ys.length - 1; i++) {
            const y0 = ys[i], y1 = ys[i + 1]
            if (ty >= Math.min(y0, y1) && ty <= Math.max(y0, y1)) {
                const f = Math.abs(y1 - y0) < 1e-6 ? 0 : (ty - y0) / (y1 - y0)
                const x = xs[i] + (xs[i + 1] - xs[i]) * f
                if (x > best) best = x
            }
        }
        return best
    }

    // Campiona la Bezier in una LUT (semi-larghezza in unita' hx per ny in [yMin,yMax])
    const buildProfile = () => {
        const M = 256
        const ys = new Array(M), xs = new Array(M)
        for (let i = 0; i < M; i++) {
            const t = i / (M - 1)
            xs[i] = Math.max(0, cubic(bezierCtrl[0].x, bezierCtrl[1].x, bezierCtrl[2].x, bezierCtrl[3].x, t))
            ys[i] = cubic(bezierCtrl[0].y, bezierCtrl[1].y, bezierCtrl[2].y, bezierCtrl[3].y, t)
        }
        profileYMin = Math.min(ys[0], ys[M - 1])
        profileYMax = Math.max(ys[0], ys[M - 1])
        if (profileYMax - profileYMin < 1e-3) profileYMax = profileYMin + 1e-3
        const data = new Float32Array(PROFILE_N * 4)
        for (let j = 0; j < PROFILE_N; j++) {
            const ty = profileYMin + (j / (PROFILE_N - 1)) * (profileYMax - profileYMin)
            const w = sampleXatY(ys, xs, ty)
            data[j * 4] = w
            data[j * 4 + 1] = w
            data[j * 4 + 2] = w
            data[j * 4 + 3] = 1
        }
        if (!profileTex) {
            profileTex = new THREE.DataTexture(data, PROFILE_N, 1, THREE.RGBAFormat, THREE.FloatType)
            profileTex.magFilter = THREE.LinearFilter
            profileTex.minFilter = THREE.LinearFilter
        } else {
            profileTex.image.data = data
        }
        profileTex.needsUpdate = true
    }

    // --- Debug: outline di sagoma repulsiva (rosso) e capsula dura (azzurro).
    // Attivo con ?debug=true, toggle col tasto B ---
    let debugCtx = null
    if (isDebug) {
        const debugCanvas = document.createElement('canvas')
        debugCanvas.className = 'carbon-debug-canvas'
        document.body.appendChild(debugCanvas)
        debugCtx = debugCanvas.getContext('2d')
        window.addEventListener('keydown', (e) => {
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return
            if (e.key === 'b' || e.key === 'B') {
                debugCanvas.style.display = debugCanvas.style.display === 'none' ? '' : 'none'
                return
            }
            // Dolly della camera sull'asse z per ispezionare meglio la linea
            const cam = window._camera
            if (!cam) return
            if (e.key === '-' || e.key === '_') cam.position.z = Math.min(cam.position.z + 4, 400)
            else if (e.key === '=' || e.key === '+') cam.position.z = Math.max(cam.position.z - 4, 4)
            else if (e.key === '0') cam.position.z = 18
            else return
            if (window._controls) window._controls.update()
        })

        // Drag & drop delle 4 maniglie: rimodella la curva di repulsione.
        // Il canvas ha pointer-events:none, quindi ascoltiamo su window e
        // catturiamo solo quando si afferra una maniglia (nessun blocco dello scroll)
        const screenToNorm = (px, py) => {
            const vw = window.innerWidth, vh = window.innerHeight
            const camZ = window._camera ? window._camera.position.z : CAMERA_Z
            const worldHalfH = Math.tan((CAMERA_FOV * Math.PI) / 360) * camZ
            const worldHalfW = worldHalfH * (vw / vh)
            const wx = (px / vw * 2 - 1) * worldHalfW
            const wy = (1 - py / vh * 2) * worldHalfH
            return { nx: (wx - st.x) / (st.hx || 1e-3), ny: (wy - st.y) / (st.hy || 1e-3) }
        }
        window.addEventListener('pointerdown', (e) => {
            if (debugCanvas.style.display === 'none' || !st.active || st.strength < 0.01) return
            let hit = -1, bestD = 18 * 18
            for (const h of handleScreen) {
                const dx = e.clientX - h.x, dy = e.clientY - h.y
                const d = dx * dx + dy * dy
                if (d < bestD) { bestD = d; hit = h.i }
            }
            if (hit >= 0) { dragIndex = hit; e.preventDefault() }
        })
        window.addEventListener('pointermove', (e) => {
            if (dragIndex < 0) return
            const { nx, ny } = screenToNorm(e.clientX, e.clientY)
            const p = bezierCtrl[dragIndex]
            p.x = dragIndex === 0 ? 0 : Math.max(0, nx) // la punta resta sull'asse
            p.y = ny
            profileDirty = true
            e.preventDefault()
        })
        const endDrag = () => {
            if (dragIndex < 0) return
            dragIndex = -1
            try { localStorage.setItem(BEZIER_KEY, JSON.stringify(bezierCtrl)) } catch (e) {}
        }
        window.addEventListener('pointerup', endDrag)
        window.addEventListener('pointercancel', endDrag)
    }

    const drawDebugShapes = () => {
        const canvas = debugCtx.canvas
        if (canvas.style.display === 'none') return
        const vw = window.innerWidth
        const vh = window.innerHeight
        if (canvas.width !== vw) canvas.width = vw
        if (canvas.height !== vh) canvas.height = vh
        debugCtx.clearRect(0, 0, vw, vh)
        // La proiezione dell'overlay segue la z REALE della camera, cosi' quando in
        // debug la si allontana la linea resta allineata alle particelle
        const camZ = window._camera ? window._camera.position.z : CAMERA_Z
        const worldHalfH = Math.tan((CAMERA_FOV * Math.PI) / 360) * camZ
        const worldHalfW = worldHalfH * (vw / vh)
        const toPx = (wx, wy) => [(wx / worldHalfW * 0.5 + 0.5) * vw, (0.5 - wy / worldHalfH * 0.5) * vh]

        // HUD: livello di zoom e scorciatoie
        debugCtx.font = '11px monospace'
        debugCtx.textBaseline = 'top'
        debugCtx.fillStyle = 'rgba(255, 220, 140, 0.9)'
        debugCtx.fillText(`cam z: ${camZ.toFixed(0)}   [ − / = zoom · 0 reset · B overlay ]`, 12, 12)

        // Cornice del viewport di DEFAULT (a cam z=18): riferimento dei bordi
        // schermo mentre si zooma out (in world space, proiettata con la z live)
        const defH = Math.tan((CAMERA_FOV * Math.PI) / 360) * CAMERA_Z
        const defW = defH * (vw / vh)
        const [fx0, fy0] = toPx(-defW, defH)
        const [fx1, fy1] = toPx(defW, -defH)
        debugCtx.save()
        debugCtx.setLineDash([6, 6])
        debugCtx.strokeStyle = 'rgba(255, 255, 255, 0.4)'
        debugCtx.lineWidth = 1
        debugCtx.strokeRect(fx0, fy0, fx1 - fx0, fy1 - fy0)
        debugCtx.setLineDash([])
        debugCtx.restore()

        handleScreen.length = 0
        smoothed.forEach((s) => {
            if (!s.active || s.strength < 0.01) return
            const bx = (t) => cubic(bezierCtrl[0].x, bezierCtrl[1].x, bezierCtrl[2].x, bezierCtrl[3].x, t)
            const by = (t) => cubic(bezierCtrl[0].y, bezierCtrl[1].y, bezierCtrl[2].y, bezierCtrl[3].y, t)

            // Contorno rosso: fianco destro dalla curva + specchio a sinistra
            const M = 96
            debugCtx.beginPath()
            for (let k = 0; k <= M; k++) {
                const t = k / M
                const [px, py] = toPx(s.x + Math.max(0, bx(t)) * s.hx, s.y + by(t) * s.hy)
                k === 0 ? debugCtx.moveTo(px, py) : debugCtx.lineTo(px, py)
            }
            for (let k = M; k >= 0; k--) {
                const t = k / M
                const [px, py] = toPx(s.x - Math.max(0, bx(t)) * s.hx, s.y + by(t) * s.hy)
                debugCtx.lineTo(px, py)
            }
            debugCtx.closePath()
            debugCtx.strokeStyle = 'rgba(255, 80, 80, 0.85)'
            debugCtx.lineWidth = 1
            debugCtx.stroke()

            // Poligono di controllo (giallo tenue) + 4 maniglie trascinabili
            debugCtx.beginPath()
            bezierCtrl.forEach((p, i) => {
                const [px, py] = toPx(s.x + p.x * s.hx, s.y + p.y * s.hy)
                i === 0 ? debugCtx.moveTo(px, py) : debugCtx.lineTo(px, py)
            })
            debugCtx.strokeStyle = 'rgba(255, 200, 80, 0.35)'
            debugCtx.stroke()
            bezierCtrl.forEach((p, i) => {
                const [px, py] = toPx(s.x + p.x * s.hx, s.y + p.y * s.hy)
                handleScreen.push({ x: px, y: py, i })
                debugCtx.beginPath()
                debugCtx.arc(px, py, 7, 0, Math.PI * 2)
                debugCtx.fillStyle = i === dragIndex ? 'rgba(255, 230, 120, 0.95)' : 'rgba(255, 160, 60, 0.9)'
                debugCtx.fill()
                debugCtx.strokeStyle = 'rgba(0, 0, 0, 0.6)'
                debugCtx.stroke()

                // Coordinate normalizzate del punto (da comunicare per fissarle nel preset)
                const label = `P${i}  ${p.x.toFixed(2)}, ${p.y.toFixed(2)}`
                debugCtx.font = '12px monospace'
                debugCtx.textBaseline = 'middle'
                debugCtx.lineWidth = 3
                debugCtx.strokeStyle = 'rgba(0, 0, 0, 0.8)'
                debugCtx.strokeText(label, px + 11, py)
                debugCtx.fillStyle = 'rgba(255, 225, 150, 0.98)'
                debugCtx.fillText(label, px + 11, py)
            })

            // Capsula dura sul testo reale (solo in modalita' solida)
            if (params.textAvoidSolid !== false) {
                const [x0, y0] = toPx(s.cx - s.chx, s.cy + s.chy)
                const [x1, y1] = toPx(s.cx + s.chx, s.cy - s.chy)
                const r = Math.max(0, Math.min(x1 - x0, y1 - y0) / 2)
                debugCtx.beginPath()
                debugCtx.roundRect(x0, y0, x1 - x0, y1 - y0, r)
                debugCtx.strokeStyle = 'rgba(80, 200, 255, 0.9)'
                debugCtx.stroke()
            }
        })

        // Barre di aspirazione: posizione + dimensione numeriche (si spostano coi
        // gizmo 3D — frecce per traslare, tasto S per scalare, G per tornare a traslare)
        ;[['suctionBar0Pos', 'suctionBar0Size', 'BAR0'], ['suctionBar1Pos', 'suctionBar1Size', 'BAR1']].forEach(([pk, sk, name]) => {
            const p = params[pk], sz = params[sk]
            if (!p || !sz) return
            const [px, py] = toPx(p.x, p.y)
            debugCtx.beginPath()
            debugCtx.arc(px, py, 3, 0, Math.PI * 2)
            debugCtx.fillStyle = 'rgba(120, 220, 140, 0.95)'
            debugCtx.fill()
            const label = `${name}  pos ${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)}   size ${sz.x.toFixed(2)} x ${sz.y.toFixed(2)} x ${sz.z.toFixed(2)}`
            debugCtx.font = '12px monospace'
            debugCtx.textBaseline = 'middle'
            debugCtx.lineWidth = 3
            debugCtx.strokeStyle = 'rgba(0, 0, 0, 0.8)'
            debugCtx.strokeText(label, px + 10, py - 12)
            debugCtx.fillStyle = 'rgba(150, 235, 170, 0.98)'
            debugCtx.fillText(label, px + 10, py - 12)
        })
    }

    // --- Coreografia a scrub. La sagoma capsula/forze è unica, con le
    // dimensioni della seconda scritta: cresce in apertura, si restringe in
    // chiusura, e nel mezzo resta fissa (nessun morph tra i testi).
    // La simulazione delle particelle parte al fade-in del testo 1.
    // 0.044-0.191  la sagoma cresce in orizzontale (da fessura a piena)
    // 0.191-0.266  fade-in testo 1 (+ avvio simulazione)
    // 0.448-0.519  fade-out testo 1
    // 0.581-0.652  fade-in testo 2
    // 0.840-0.898  fade-out testo 2 (hold del testo 2 esteso del 150%)
    // 0.898-1.000  la sagoma si restringe fino a sparire
    const update = (deltaTime) => {
        const c = window._getCompute && window._getCompute()
        if (!c) return
        const vU = c.velocityVariable.material.uniforms
        const pU = c.particlesVariable.material.uniforms

        // Profilo di repulsione da curva di Bezier: (ri)costruisce la LUT e la
        // attiva su entrambi i materiali. Solo quando i punti cambiano (drag)
        if (profileDirty) {
            buildProfile()
            for (const u of [vU, pU]) {
                u.uProfileEnabled.value = 1
                u.uProfileTex.value = profileTex
                u.uProfileYMin.value = profileYMin
                u.uProfileYMax.value = profileYMax
            }
            profileDirty = false
        }

        vU.uTextAvoidRadius.value = params.textAvoidRadius ?? 2.0
        pU.uSpawnBottomOnly.value = spawnRegion.value
        const worldHalfH = Math.tan((CAMERA_FOV * Math.PI) / 360) * CAMERA_Z
        const worldHalfW = worldHalfH * (window.innerWidth / window.innerHeight)
        pU.uSpawnBottomSpread.value = worldHalfW * 1.6
        vU.uDomainHalfW.value = worldHalfW * 1.6
        pU.uDomainHalfW.value = worldHalfW * 1.6

        // Progresso di scroll
        const doc = document.documentElement
        const maxScroll = Math.max(doc.scrollHeight - window.innerHeight, 1)
        const p = clamp01((window.scrollY || doc.scrollTop) / maxScroll)

        // Fasi
        // Fuori debug la sagoma parte gia' espansa (niente apertura da chiusa a
        // inizio scroll); in debug resta l'espansione per poterla ispezionare.
        // Lo shrink finale resta attivo in entrambi i casi.
        const grow = isDebug ? smooth(ramp(p, 0.044, 0.191)) : 1
        const shrink = smooth(ramp(p, 0.898, 1.0))
        const widthScale = grow * (1 - shrink)
        const alpha1 = ramp(p, 0.191, 0.266) * (1 - ramp(p, 0.448, 0.519))
        const alpha2 = ramp(p, 0.581, 0.652) * (1 - ramp(p, 0.84, 0.898))

        // Opacita' delle particelle in puro scrub dallo scroll: prima appare
        // il testo 1 (fade-in 0.191-0.266), poi la tenda di visibilita' delle
        // particelle entra a scritta gia' visibile e si ritira tornando in
        // cima. Leggero inseguimento per ammorbidire gli scatti.
        if (!isDebug) {
            const fadeTarget = ramp(p, 0.27, 0.42)
            const fs = window._fadeState
            fs.value += (fadeTarget - fs.value) * (1 - Math.exp(-4 * (deltaTime || 0.016)))
        }

        wrap1.style.opacity = alpha1
        wrap2.style.opacity = alpha2

        // Sagoma unica: sempre le dimensioni della seconda scritta
        const padding = params.textAvoidPadding ?? 28
        const corePadding = params.textCorePadding ?? 8
        const r2 = textEl2.getBoundingClientRect()
        const b2 = screenRectToWorldBox(r2, padding)
        const c2 = screenRectToWorldBox(r2, corePadding, 1.0)

        const wMin = 0.02 // fessura iniziale (quasi una linea verticale)
        // La sagoma delle forze sta 70px piu' in alto del testo (flusso
        // discendente): la cupola inferiore copre meglio la scritta e le
        // particelle non la sfiorano
        const forceOffsetY = (70 / window.innerHeight) * (2 * worldHalfH)
        const target = {
            x: b2.x,
            y: b2.y + forceOffsetY,
            hx: b2.hx * Math.max(widthScale, wMin),
            hy: b2.hy,
            cx: c2.x,
            cy: c2.y,
            chx: c2.hx * Math.max(widthScale, wMin),
            chy: c2.hy,
            strength: widthScale > 0.001 ? (params.textAvoidStrength ?? 0) : 0,
        }

        // Inseguimento lento: anche con scroll violenti la sagoma si muove e si
        // ridimensiona in modo graduale, spostando le particelle senza spararle via.
        // In debug la prima attivazione parte da una fessura a forza zero e si
        // gonfia; fuori debug scatta direttamente alla forma piena (gia' espansa).
        if (!st.active && target.strength > 0.01) {
            Object.assign(st, target)
            if (isDebug) {
                st.hx = Math.min(target.hx, 0.05)
                st.chx = Math.min(target.chx, 0.02)
                st.strength = 0
            }
            st.active = true
        }
        const alpha = 1 - Math.exp(-6 * (deltaTime || 0.016))
        st.x += (target.x - st.x) * alpha
        st.y += (target.y - st.y) * alpha
        st.hx += (target.hx - st.hx) * alpha
        st.hy += (target.hy - st.hy) * alpha
        st.cx += (target.cx - st.cx) * alpha
        st.cy += (target.cy - st.cy) * alpha
        st.chx += (target.chx - st.chx) * alpha
        st.chy += (target.chy - st.chy) * alpha
        st.strength += (target.strength - st.strength) * alpha
        if (st.strength < 0.005 && target.strength === 0) st.active = false
        st.tipUp = st.hy * (params.textAvoidTip ?? 1.6)
        st.tipDown = st.hy * (params.textAvoidTipDown ?? 4.5)

        // Scrittura uniform (sagoma unica: slot 0; slot 1 spento)
        vU.uTextBox0.value.set(st.x, st.y, st.hx, st.hy)
        pU.uTextBox0.value.copy(vU.uTextBox0.value)
        pU.uTextCore0.value.set(st.cx, st.cy, st.chx, st.chy)
        vU.uTextTips0.value.set(st.tipUp, st.tipDown)
        pU.uTextTips0.value.copy(vU.uTextTips0.value)
        vU.uTextBox0Strength.value = st.strength
        pU.uTextBox0Strength.value = st.strength
        vU.uTextBox1Strength.value = 0
        pU.uTextBox1Strength.value = 0

        // Il bias di spawn verso i bordi segue l'espansione dell'area repulsiva:
        // 0 = sagoma chiusa (spawn uniforme), 1 = sagoma a piena larghezza (bias pieno)
        pU.uSpawnEdgeAmount.value = clamp01(widthScale)

        if (debugCtx) drawDebugShapes()
    }

    return { update }
}
